import { careApiGet, normalizeListResponse } from './careApiClient';
import { resolveCollectionFileUrl } from './collectionFiles';

/** Adjust paths to match your server routes. */
export const CARE_API_PATHS = {
  doctorProfiles: '/doctor-profiles',
  patientProfiles: '/patient-profiles',
  pharmacyProfiles: '/pharmacy-profiles',
  transactions: '/payment-transactions',
  supportLogs: '/support-logs',
  staffAccounts: '/staff-accounts'
};

export async function fetchDoctorProfilesList(token, params) {
  const payload = await careApiGet(CARE_API_PATHS.doctorProfiles, { token, params });
  return mapDoctorRows(normalizeListResponse(payload));
}

function mapDoctorRows(list) {
  return list.map((item) => {
    const userFromExpand = item.expand?.user;
    const userFromField = typeof item.user === 'object' && item.user != null ? item.user : null;
    const user = userFromExpand || userFromField || null;

    return {
      ...item,
      user,
      user_id: user?.id ?? (typeof item.user === 'string' ? item.user : ''),
      user_name: user?.name ?? '',
      user_email: user?.email ?? '',
      user_verified: Boolean(user?.verified)
    };
  });
}

export async function fetchPatientProfilesList(token, params) {
  const payload = await careApiGet(CARE_API_PATHS.patientProfiles, { token, params });
  return mapPatientRows(normalizeListResponse(payload));
}

function mapPatientRows(list) {
  return list.map((item) => {
    const userFromExpand = item.expand?.user;
    const userFromField = typeof item.user === 'object' && item.user != null ? item.user : null;
    const user = userFromExpand || userFromField || null;

    return {
      ...item,
      user,
      user_id: user?.id ?? (typeof item.user === 'string' ? item.user : ''),
      user_name: user?.name ?? '',
      user_email: user?.email ?? '',
      user_verified: Boolean(user?.verified),
      avatar_url: resolveCollectionFileUrl({
        serverUrl: "https://pbs.nvoisyshealth.com/api",
        collectionName: item.collectionName || 'patient_profile',
        recordId: item.id,
        fileName: item.avatar
      })
    };
  });
}

export async function fetchPharmacyProfilesList(token, params) {
  const payload = await careApiGet(CARE_API_PATHS.pharmacyProfiles, { token, params });
  return normalizeListResponse(payload);
}

export async function fetchTransactionsList(token, params) {
  const payload = await careApiGet(CARE_API_PATHS.transactions, { token, params });
  return mapTransactionRows(normalizeListResponse(payload));
}

function displayTransactionParty(userRecord, fallback = {}) {
  const name = userRecord?.name?.trim();
  if (name) return name;
  const email = userRecord?.email?.trim() || String(fallback.email || '').trim();
  if (email) return email;
  const phone = userRecord?.phone?.trim() || String(fallback.phone || '').trim();
  if (phone) return phone;
  return '';
}

function mapTransactionRows(list) {
  return list.map((item) => {
    const doctorUser = item.expand?.doctor;
    const patientUser = item.expand?.patient;

    const patientName =
      displayTransactionParty(patientUser, {
        email: item.customer_email,
        phone: item.customer_phone
      }) ||
      item.patientName ||
      item.patient_name ||
      '';

    const doctorName =
      displayTransactionParty(doctorUser) ||
      item.doctorName ||
      item.doctor_name ||
      '';

    let amountInr = 0;
    if (item.amount_inr != null && item.amount_inr !== '') {
      amountInr = Number(item.amount_inr);
    } else if (item.amount_paise != null && item.amount_paise !== '') {
      amountInr = Number(item.amount_paise) / 100;
    } else if (item.amount != null && item.amount !== '') {
      amountInr = Number(item.amount);
    }

    const currency = String(item.currency || 'INR').toUpperCase();
    const amountText =
      currency === 'INR'
        ? `₹${Number.isFinite(amountInr) ? amountInr.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '0'}`
        : `${currency} ${Number.isFinite(amountInr) ? amountInr.toFixed(2) : '0.00'}`;

    const transactionAt = item.paid_at || item.transactionAt || item.created;

    return {
      id: item.transactionId || item.id,
      patientName: patientName || '—',
      doctorName: doctorName || '—',
      patientEmail: patientUser?.email || item.customer_email || '',
      amount: Number.isFinite(amountInr) ? amountInr : 0,
      amountText,
      currency,
      status: item.status || 'unknown',
      kind: item.kind || '',
      transactionAt,
      created: item.created,
      updated: item.updated
    };
  });
}

export async function fetchSupportLogsList(token, params) {
  const payload = await careApiGet(CARE_API_PATHS.supportLogs, { token, params });
  return mapSupportLogRows(normalizeListResponse(payload));
}

function mapSupportLogRows(list) {
  return list.map((item) => ({
    id: item.id,
    staffEmail: item.staffEmail || item.staff_email,
    action: item.action,
    createdAt: item.createdAt || item.created,
    created: item.created
  }));
}

export async function fetchStaffAccountsList(token, params) {
  const payload = await careApiGet(CARE_API_PATHS.staffAccounts, { token, params });
  return mapStaffRows(normalizeListResponse(payload));
}

function mapStaffRows(list) {
  return list.map((item) => ({
    id: item.id,
    name: item.name,
    email: item.email,
    role: item.role,
    status: item.status
  }));
}

/** Optional list routes: return [] if the server has no route yet or returns an error. */
export async function fetchTransactionsListOrEmpty(token, params) {
  try {
    return await fetchTransactionsList(token, params);
  } catch {
    return [];
  }
}

export async function fetchSupportLogsListOrEmpty(token, params) {
  try {
    return await fetchSupportLogsList(token, params);
  } catch {
    return [];
  }
}

export async function fetchStaffAccountsListOrEmpty(token, params) {
  try {
    return await fetchStaffAccountsList(token, params);
  } catch {
    return [];
  }
}

